// Admin tab for managing hole hazards (water, bunkers, dogleg pins, layup
// zones, OB, trees). Two input modes per hole:
//   • paste lat,lng from Google Maps
//   • use the player's current GPS (handy when admin is on-course)
//
// Backend:
//   POST   /api/courses/{courseId}/holes/{n}/hazards
//   DELETE /api/courses/{courseId}/holes/{n}/hazards/{hazardId}
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Flag, Trash2, Crosshair, ExternalLink } from 'lucide-react';
import { HAZARD_ICON, HAZARD_LABEL } from '@/lib/hazards';

const TYPES = ['water', 'bunker', 'dogleg', 'layup', 'trees', 'ob'];

// Smart default radius (yards) per hazard type — see Caddie strategy logic
// for how this affects carry / layup distance recommendations. Trees are
// near-point objects; lakes are large; bunkers depend on size, but the most
// common case (medium fairway bunker) gets 8y.
const DEFAULT_RADIUS = {
  water: 18,
  bunker: 8,
  dogleg: 5,
  layup: 5,
  trees: 4,
  ob: 5,
};

export default function HazardsTab() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [course, setCourse] = useState(null);
  const [hole, setHole] = useState(null);                 // selected hole number
  const [type, setType] = useState('bunker');
  const [coords, setCoords] = useState('');
  const [radius, setRadius] = useState(String(DEFAULT_RADIUS.bunker));
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  // Optional edge mode — for elongated water/big bunkers, pin near + far edges
  // along the line of play instead of relying on a centre+radius approximation.
  const [edgeMode, setEdgeMode] = useState(false);
  const [frontCoords, setFrontCoords] = useState('');
  const [backCoords, setBackCoords] = useState('');

  useEffect(() => {
    axios.get(`${API}/courses`).then(r => setCourses(r.data || [])).catch(() => {});
  }, []);

  const refreshCourse = async () => {
    if (!courseId) return;
    const r = await axios.get(`${API}/courses/${courseId}`);
    setCourse(r.data);
  };
  useEffect(() => { refreshCourse(); /* eslint-disable-next-line */ }, [courseId]);

  const holes = (() => {
    if (!course) return [];
    const tee = (course.tees || [])[0];
    return tee?.holes || course.holes || [];
  })();

  // When the type changes, snap the radius input to the smart default for
  // that type — admins can still override before saving.
  const handleTypeChange = (newType) => {
    setType(newType);
    setRadius(String(DEFAULT_RADIUS[newType] ?? 10));
  };

  const captureGPS = (target = 'center') => {
    if (!navigator.geolocation) { toast.error('GPS not available'); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const fmt = `${p.coords.latitude.toFixed(6)}, ${p.coords.longitude.toFixed(6)}`;
        if (target === 'front')      setFrontCoords(fmt);
        else if (target === 'back')  setBackCoords(fmt);
        else                         setCoords(fmt);
        toast.success(`GPS locked → ${target} (±${Math.round(p.coords.accuracy)}m)`);
        setGpsBusy(false);
      },
      () => { toast.error('Could not get GPS'); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const parseCoords = (raw) => {
    const m = raw.replace(/[°NSEW]/gi, '').match(/(-?\d+\.?\d*)\s*[, ]\s*(-?\d+\.?\d*)/);
    if (!m) return null;
    const lat = parseFloat(m[1]); const lng = parseFloat(m[2]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const addHazard = async () => {
    if (!hole) { toast.error('Pick a hole first'); return; }
    const p = parseCoords(coords);
    if (!p) { toast.error('Centre coords invalid — paste like 47.65, -117.37'); return; }
    const payload = {
      type, lat: p.lat, lng: p.lng,
      radius_y: parseFloat(radius) || 10,
      label: label.trim() || undefined,
    };
    if (edgeMode) {
      const f = parseCoords(frontCoords);
      const b = parseCoords(backCoords);
      if (!f || !b) {
        toast.error('Edge mode needs both Front and Back coords');
        return;
      }
      payload.front_lat = f.lat; payload.front_lng = f.lng;
      payload.back_lat  = b.lat; payload.back_lng  = b.lng;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/courses/${courseId}/holes/${hole}/hazards`, payload);
      toast.success(`✓ ${HAZARD_LABEL[type]} added on hole ${hole}${edgeMode ? ' (with edges)' : ''}`);
      setCoords(''); setLabel(''); setFrontCoords(''); setBackCoords('');
      setRadius(String(DEFAULT_RADIUS[type] ?? 10));
      await refreshCourse();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const removeHazard = async (holeNum, hazardId) => {
    if (!window.confirm('Delete this hazard?')) return;
    try {
      await axios.delete(`${API}/courses/${courseId}/holes/${holeNum}/hazards/${hazardId}`);
      toast.success('Hazard removed');
      await refreshCourse();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Inline radius edit on existing hazards. Tracks editingRadius[hazard_id] →
  // pending input value. Save patches the backend; cancel reverts.
  const [editingRadius, setEditingRadius] = useState({});
  const startEditRadius = (z) =>
    setEditingRadius(s => ({ ...s, [z.hazard_id]: String(z.radius_y || 10) }));
  const cancelEditRadius = (id) =>
    setEditingRadius(s => { const c = { ...s }; delete c[id]; return c; });
  const saveEditRadius = async (holeNum, hazardId) => {
    const v = parseFloat(editingRadius[hazardId]);
    if (Number.isNaN(v) || v < 1 || v > 80) {
      toast.error('Radius must be 1-80 yards');
      return;
    }
    try {
      await axios.patch(
        `${API}/courses/${courseId}/holes/${holeNum}/hazards/${hazardId}`,
        { radius_y: v }
      );
      toast.success(`Radius updated to ${v}y`);
      cancelEditRadius(hazardId);
      await refreshCourse();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  // Group hazards across all holes for the listing.
  const hazardsByHole = {};
  for (const h of holes) {
    const n = h.hole || h.number;
    if ((h.hazards || []).length) hazardsByHole[n] = h.hazards;
  }

  return (
    <div className="space-y-4" data-testid="hazards-tab-content">
      <Card className="border-[#E2E3DD] shadow-none bg-[#F4E9D8]/40">
        <CardContent className="py-4 text-sm text-[#1B3C35] space-y-1.5">
          <p className="font-bold flex items-center gap-1.5">
            <Flag className="h-4 w-4 text-[#C96A52]" /> Hazards &amp; Strategic Zones
          </p>
          <p className="text-xs text-[#6B6E66]">
            Pin water, bunkers, OB, dogleg corners and layup zones so the Caddie can
            advise carry distances and short strategy on every shot.
          </p>
        </CardContent>
      </Card>

      <div>
        <Label className="text-[#1B3C35] text-sm font-bold">Course</Label>
        <Select value={courseId} onValueChange={setCourseId}>
          <SelectTrigger className="mt-1 border-[#E2E3DD]" data-testid="hazards-course-select">
            <SelectValue placeholder="Choose a course..." />
          </SelectTrigger>
          <SelectContent>
            {courses.map(c => (
              <SelectItem key={c.course_id} value={c.course_id}>
                {c.course_name || c.name} {c.total_par ? `(Par ${c.total_par})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {courseId && holes.length > 0 && (
        <Card className="border-[#E2E3DD] shadow-none">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-bold text-[#1B3C35]" style={{ fontFamily: 'Outfit' }}>
              Add a hazard
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-[#6B6E66]">Hole</Label>
                <Select value={hole ? String(hole) : ''} onValueChange={(v) => setHole(parseInt(v, 10))}>
                  <SelectTrigger className="mt-1 h-9 border-[#E2E3DD]" data-testid="hazard-hole-select">
                    <SelectValue placeholder="Pick hole" />
                  </SelectTrigger>
                  <SelectContent>
                    {holes.map(h => {
                      const n = h.hole || h.number;
                      return <SelectItem key={n} value={String(n)}>Hole {n}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-[#6B6E66]">Type</Label>
                <Select value={type} onValueChange={handleTypeChange}>
                  <SelectTrigger className="mt-1 h-9 border-[#E2E3DD]" data-testid="hazard-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map(t => (
                      <SelectItem key={t} value={t}>
                        {HAZARD_ICON[t]} {HAZARD_LABEL[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-[#6B6E66]">
                {edgeMode ? 'Centre (lat, lng)' : 'Coordinates (lat, lng)'}
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={coords}
                  onChange={e => setCoords(e.target.value)}
                  placeholder="47.651506, -117.373635"
                  className="border-[#E2E3DD] text-xs font-mono"
                  data-testid="hazard-coords-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#C96A52] text-[#C96A52] shrink-0"
                  onClick={() => captureGPS('center')}
                  disabled={gpsBusy}
                  data-testid="hazard-use-gps">
                  <Crosshair className={`h-3.5 w-3.5 mr-1 ${gpsBusy ? 'animate-spin' : ''}`} />
                  My GPS
                </Button>
              </div>
              <p className="text-[10px] text-[#6B6E66] mt-1">
                Right-click the spot in Google Maps to copy coordinates, or tap "My GPS" if you're standing on it.
              </p>
            </div>

            {/* Edge mode toggle — adds Front + Back inputs for accurate carry/safe-past distances on water and big bunkers. */}
            <button
              type="button"
              onClick={() => setEdgeMode(v => !v)}
              className="text-[11px] text-[#C96A52] hover:underline font-bold"
              data-testid="hazard-edge-toggle">
              {edgeMode ? '− Hide edges (use radius only)' : '+ Pin edges (front & back) for higher accuracy'}
            </button>

            {edgeMode && (
              <div className="space-y-2 p-2 rounded-lg bg-[#F4E9D8]/40 border border-[#C96A52]/20">
                <p className="text-[10px] text-[#6B6E66] leading-snug">
                  Best for water hazards and large bunkers. Pin the edge closest to the tee
                  (Front) and the edge farthest from the tee (Back). Caddie will compute exact
                  carry distance to clear the hazard.
                </p>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-bold text-[#C96A52]">
                    Front edge (closer to tee)
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={frontCoords}
                      onChange={e => setFrontCoords(e.target.value)}
                      placeholder="lat, lng"
                      className="border-[#E2E3DD] text-xs font-mono"
                      data-testid="hazard-front-input"
                    />
                    <Button
                      variant="outline" size="sm"
                      className="border-[#C96A52] text-[#C96A52] shrink-0"
                      onClick={() => captureGPS('front')}
                      disabled={gpsBusy}
                      data-testid="hazard-front-gps">
                      <Crosshair className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-bold text-[#4A5D23]">
                    Back edge (farther from tee)
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={backCoords}
                      onChange={e => setBackCoords(e.target.value)}
                      placeholder="lat, lng"
                      className="border-[#E2E3DD] text-xs font-mono"
                      data-testid="hazard-back-input"
                    />
                    <Button
                      variant="outline" size="sm"
                      className="border-[#4A5D23] text-[#4A5D23] shrink-0"
                      onClick={() => captureGPS('back')}
                      disabled={gpsBusy}
                      data-testid="hazard-back-gps">
                      <Crosshair className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-[#6B6E66]">
                  Radius (yards)
                </Label>
                <Input
                  type="number"
                  value={radius}
                  onChange={e => setRadius(e.target.value)}
                  min="1" max="80" step="1"
                  className="h-9 mt-1 border-[#E2E3DD]"
                  data-testid="hazard-radius-input"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-[#6B6E66]">
                  Label (optional)
                </Label>
                <Input
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="Pond, Front bunker..."
                  className="h-9 mt-1 border-[#E2E3DD]"
                  data-testid="hazard-label-input"
                />
              </div>
            </div>

            <Button
              className="w-full bg-[#1B3C35] hover:bg-[#1B3C35]/90"
              disabled={busy || !hole || !coords.trim()}
              onClick={addHazard}
              data-testid="hazard-save-btn">
              {busy ? 'Saving…' : 'Add hazard'}
            </Button>
          </CardContent>
        </Card>
      )}

      {courseId && Object.keys(hazardsByHole).length > 0 && (
        <Card className="border-[#E2E3DD] shadow-none">
          <CardContent className="p-0">
            <div className="px-3 py-2 bg-[#E8E9E3]/50 text-[10px] font-bold text-[#6B6E66] uppercase tracking-wider">
              Existing hazards
            </div>
            <div className="divide-y divide-[#E2E3DD]">
              {Object.entries(hazardsByHole)
                .sort((a, b) => Number(a[0]) - Number(b[0]))
                .map(([n, list]) => (
                <div key={n} className="px-3 py-2.5">
                  <p className="text-xs font-bold text-[#1B3C35] mb-1.5">Hole {n}</p>
                  <div className="space-y-1">
                    {list.map(z => (
                      <div key={z.hazard_id}
                        className="flex items-center gap-2 text-xs"
                        data-testid={`hazard-row-${z.hazard_id}`}>
                        <span className="text-base">{HAZARD_ICON[z.type] || '📍'}</span>
                        <span className="font-semibold text-[#1B3C35] capitalize">
                          {z.label || HAZARD_LABEL[z.type] || z.type}
                        </span>
                        {z.front_lat != null && z.back_lat != null && (
                          <span className="text-[9px] uppercase tracking-wider font-bold text-[#4A5D23] bg-[#4A5D23]/10 px-1.5 py-0.5 rounded">
                            edges
                          </span>
                        )}
                        <span className="text-[#6B6E66] tabular-nums">
                          {z.lat?.toFixed(5)}, {z.lng?.toFixed(5)}
                        </span>
                        {editingRadius[z.hazard_id] !== undefined ? (
                          <span className="flex items-center gap-1 ml-1">
                            <Input
                              type="number" min="1" max="80" step="1"
                              value={editingRadius[z.hazard_id]}
                              onChange={e => setEditingRadius(s => ({ ...s, [z.hazard_id]: e.target.value }))}
                              className="h-6 w-14 px-1 text-[10px] tabular-nums border-[#C96A52]"
                              data-testid={`hazard-radius-input-${z.hazard_id}`}
                              autoFocus
                            />
                            <button onClick={() => saveEditRadius(parseInt(n, 10), z.hazard_id)}
                              className="text-[#4A5D23] font-bold text-[10px] hover:underline"
                              data-testid={`hazard-radius-save-${z.hazard_id}`}>
                              save
                            </button>
                            <button onClick={() => cancelEditRadius(z.hazard_id)}
                              className="text-[#6B6E66] text-[10px] hover:underline">
                              ×
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => startEditRadius(z)}
                            className="text-[#6B6E66] tabular-nums hover:text-[#C96A52] hover:underline"
                            title="Click to edit radius"
                            data-testid={`hazard-radius-${z.hazard_id}`}>
                            r{Math.round(z.radius_y || 10)}y
                          </button>
                        )}
                        <a
                          href={`https://www.google.com/maps?q=${z.lat},${z.lng}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-[#C96A52] hover:underline ml-auto inline-flex items-center gap-0.5 shrink-0"
                          data-testid={`hazard-view-${z.hazard_id}`}>
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                        {(z.added_by === user?.user_id || (user?.email || '').toLowerCase() === 'pzsuave007@gmail.com') && (
                          <button
                            onClick={() => removeHazard(parseInt(n, 10), z.hazard_id)}
                            className="text-[#C96A52] hover:text-[#1B3C35] shrink-0"
                            data-testid={`hazard-delete-${z.hazard_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
